// src/pages/processifyd/ProcessifydScripts.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faList,
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydScripts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scripts, setScripts] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingScript, setEditingScript] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [loading, setLoading] = useState(false);
  
  const initialForm = {
    scriptTitle: "",
    aboutScriptMaster: "",
    scriptUsedIn: "",
    scriptTriggerPoint: "",
    scriptLanguage: "",
    linkedScriptID: "",
    script: "",
    client: "",
    active: "Yes",
  };

  const [formData, setFormData] = useState(initialForm);

  // Sample Demo Data
  const demoScripts = [
    {
      id: 1,
      scriptTitle: "Customer Thank You Email for Participation in Survey",
      aboutScriptMaster: "This is Email Script is for Customer Thank You Email for Participation in Survey",
      scriptUsedIn: "Email",
      scriptTriggerPoint: "When Survey is Over",
      scriptLanguage: "English",
      linkedScriptID: "554553355",
      script: "Dear [Participant's Name],\n\nThank you for sharing your valuable feedback with us. We appreciate your time.\nYour insights are important to us and Your feedback helps us improve and better serve you.\nIf you have any additional comments or suggestions, feel free to reply.\nWe're here to listen you.\n\nThanks again,\n21st Century Financial Services",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
    {
      id: 2,
      scriptTitle: "Loan Approval Notification SMS",
      aboutScriptMaster: "SMS notification sent to customer when loan application is approved",
      scriptUsedIn: "SMS",
      scriptTriggerPoint: "After Loan Approval",
      scriptLanguage: "English",
      linkedScriptID: "SMS-998877",
      script: "Dear [Customer Name], Your loan application has been approved. Loan Amount: ₹[Amount]. Please check your registered email for details.",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "20/01/24 14:10",
      lastUpdatedBy: "Priya Sharma",
      lastUpdateDate: "15/03/24 10:30",
    },
  ];

  const loadSample = () => {
    setScripts(demoScripts);
  };

  const handleAddNew = () => {
    setEditingScript(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (script) => {
    setEditingScript(script);
    setFormData({
      scriptTitle: script.scriptTitle,
      aboutScriptMaster: script.aboutScriptMaster || "",
      scriptUsedIn: script.scriptUsedIn || "",
      scriptTriggerPoint: script.scriptTriggerPoint || "",
      scriptLanguage: script.scriptLanguage || "",
      linkedScriptID: script.linkedScriptID || "",
      script: script.script || "",
      client: script.client || "",
      active: script.active || "Yes",
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this script? This action cannot be undone.")) return;
    setScripts((prev) => prev.filter((s) => s.id !== id));
  };

  const handleOpenScript = (script) => {
    // Update this route as per your actual script detail page
    navigate(`/processifyd-scripthub/${script.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.scriptTitle.trim() || !formData.script.trim()) {
      alert("Script Title and Script content are required.");
      return;
    }

    const newScript = {
      id: editingScript?.id || Date.now(),
      ...formData,
      createdBy: editingScript?.createdBy || "Sanjeev Kumar",
      createdDate: editingScript?.createdDate || new Date().toLocaleString("en-IN"),
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: new Date().toLocaleString("en-IN"),
    };

    if (editingScript) {
      setScripts((prev) =>
        prev.map((s) => (s.id === editingScript.id ? newScript : s))
      );
    } else {
      setScripts((prev) => [...prev, newScript]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // Filter + Pagination
  const filtered = scripts.filter(
    (s) =>
      s.scriptTitle.toLowerCase().includes(search.toLowerCase()) ||
      s.aboutScriptMaster.toLowerCase().includes(search.toLowerCase()) ||
      s.scriptUsedIn.toLowerCase().includes(search.toLowerCase()) ||
      s.client.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const [showList, setShowList] = useState(false);
    
  const handleShowMyScripts = async () => {
    try {
      setLoading(true);

      // 🔄 Replace this later with API call
      // const response = await axios.get("/api/scripts");
      // setScripts(response.data);

      // ✅ Temporary Demo Data (remove later)
      setScripts([]);

      setShowList(false);
    } catch (error) {
      console.error("Error fetching scripts:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            📝 Processifyd
          </div>
          <div className="text-xs text-gray-400">
            Scripts Master Management
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

        <button
          className="md:hidden w-12 text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          {isLoggedIn && (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="w-12 px-2 py-1 bg-red-700 rounded-md">⏻</button>
            </div>
          )}
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex flex-row gap-3">
            <button
              onClick={loadSample}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              Load Sample Scripts
            </button>
            <button
              onClick={handleShowMyScripts}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-32 lg:w-40 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faList} />
              My Scripts
            </button>
            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-32 lg:w-40 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Script
            </button>
          </div>

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by script title, used in or client..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500/60"
            />
          </div>
        </div>

        {/* Script Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((script) => (
            <div
              key={script.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300 transition-colors line-clamp-2">
                    {script.scriptTitle}
                  </h3>
                  <div className="text-sm text-gray-400 mt-1">
                    {script.scriptUsedIn} • {script.scriptLanguage}
                  </div>
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-medium ${script.active === "Yes" ? "bg-emerald-600" : "bg-red-600"}`}>
                  {script.active === "Yes" ? "Active" : "Inactive"}
                </span>
              </div>

              <p className="mt-4 text-sm text-gray-300 line-clamp-3">
                {script.aboutScriptMaster || "No description available"}
              </p>

              <div className="mt-4 text-xs text-gray-500">
                <strong>Trigger:</strong> {script.scriptTriggerPoint}
              </div>

              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenScript(script)}
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  Open
                </button>

                <button
                  onClick={() => handleEdit(script)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit Script"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>

                <button
                  onClick={() => handleDelete(script.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete Script"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search ? "No scripts match your search" : "No scripts yet. Click 'New Script' or 'Load Sample Scripts'"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {showList && (
          <div className="mt-4 bg-black text-white rounded-lg p-4">
            {loading ? (
              <p className="text-yellow-200">Loading scripts...</p>
            ) : scripts.length === 0 ? (
              <p className="text-yellow-200">No scripts found.</p>
            ) : (
              <ul className="space-y-2">
                {scripts.map((sc) => (
                  <li
                    key={sc.id}
                    className="border border-gray-700 rounded p-2"
                  >
                    <p className="font-semibold text-gray-100">{sc.stateName}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Add / Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingScript ? "Edit Script" : "New Script"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white w-12 text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm mb-1.5">Script Title <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.scriptTitle}
                    onChange={(e) => setFormData({ ...formData, scriptTitle: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. Customer Thank You Email for Participation in Survey"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">About Script Master</label>
                  <textarea
                    value={formData.aboutScriptMaster}
                    onChange={(e) => setFormData({ ...formData, aboutScriptMaster: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="Short description about this script..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Script Used In <span className="text-red-400">*</span></label>
                    <input
                      required
                      value={formData.scriptUsedIn}
                      onChange={(e) => setFormData({ ...formData, scriptUsedIn: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. Email, SMS, WhatsApp"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Script Trigger Point <span className="text-red-400">*</span></label>
                    <input
                      required
                      value={formData.scriptTriggerPoint}
                      onChange={(e) => setFormData({ ...formData, scriptTriggerPoint: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. When Survey is Over"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Script Language <span className="text-red-400">*</span></label>
                    <input
                      required
                      value={formData.scriptLanguage}
                      onChange={(e) => setFormData({ ...formData, scriptLanguage: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. English"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Linked Script ID</label>
                    <input
                      value={formData.linkedScriptID}
                      onChange={(e) => setFormData({ ...formData, linkedScriptID: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. 554553355"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Client <span className="text-red-400">*</span></label>
                    <input
                      required
                      value={formData.client}
                      onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. 21st Century Financial Services"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Script <span className="text-red-400">*</span></label>
                  <textarea
                    required
                    value={formData.script}
                    onChange={(e) => setFormData({ ...formData, script: e.target.value })}
                    rows={8}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500 font-mono text-sm"
                    placeholder="Paste your script content here..."
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Active <span className="text-red-400">*</span></label>
                  <select
                    value={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                    className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="text-xs lg:text-sm w-20 lg:w-20 px-2 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="text-xs lg:text-sm w-32 lg:w-40 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingScript ? "Update Script" : "Create Script"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydScripts;